/**
 * Calendar Month Summary
 *
 * Computes owner-intuitive monthly metrics from calendar reservations only.
 * Aligns with occupied nights, payouts, and tourist tax remittance rules.
 *
 * All metrics are data-driven from already-loaded calendar reservations
 * (month-overlapping, status-filtered). No new queries.
 */

import { parseLocalDate } from './parseLocalDate.js';

const ONE_DAY_MS = 1000 * 60 * 60 * 24;

/** @param {Date} d */
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

/**
 * Total reservation nights (full stay). [checkIn, checkOut) — checkout day not occupied.
 * @param {Date} checkIn
 * @param {Date} checkOut
 * @returns {number}
 */
function totalReservationNights(checkIn, checkOut) {
  const start = startOfDay(checkIn);
  const end = startOfDay(checkOut);
  if (end <= start) return 0;
  return Math.floor((end - start) / ONE_DAY_MS);
}

/**
 * Occupied nights in month: overlap of [stayStart, stayEnd) with [monthStart, monthEndExclusive).
 * check_out is exclusive; month end is start-of-next-month (exclusive).
 * @param {Date} stayStart
 * @param {Date} stayEnd
 * @param {Date} monthStart
 * @param {Date} monthEndExclusive
 * @returns {number}
 */
function occupiedNightsInMonth(stayStart, stayEnd, monthStart, monthEndExclusive) {
  const s = stayStart.getTime();
  const e = stayEnd.getTime();
  const mStart = monthStart.getTime();
  const mEnd = monthEndExclusive.getTime();
  const overlapStart = Math.max(s, mStart);
  const overlapEnd = Math.min(e, mEnd);
  const ms = Math.max(0, overlapEnd - overlapStart);
  return Math.floor(ms / ONE_DAY_MS);
}

/**
 * Owner stay: match UI "Owner Stay" pill. Exclude forced lease (clc_lease).
 * @param {object} r - Reservation (check_in_date, status, canonical_financial_summary)
 * @returns {boolean}
 */
function isOwnerStay(r) {
  const canonical = r?.canonical_financial_summary;
  const sub = (canonical?.channel_subtype ?? '').toString().toLowerCase().trim();
  const statusR = (r?.status ?? '').toString().toLowerCase().trim();
  const statusC = (canonical?.status ?? '').toString().toLowerCase().trim();

  if (sub === 'clc_lease' || canonical?.payout_method === 'clc_lease_computed') {
    return false;
  }
  if (canonical?.master_channel_key === 'owner') return true;
  if (['owner_stay', 'owner_use'].includes(sub)) return true;
  if (statusR === 'ownerstay') return true;
  if (statusC === 'ownerstay') return true;
  return false;
}

/**
 * Compute calendar month summary from reservations.
 *
 * @param {object} opts
 * @param {number} opts.year
 * @param {number} opts.monthIndex 0–11
 * @param {Array<object>} opts.reservations Reservations overlapping the month (check_in_date, check_out_date, canonical_financial_summary)
 * @returns {object}
 */
export function computeCalendarMonthSummary({ year, monthIndex, reservations }) {
  const totalDaysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  const monthStart = startOfDay(new Date(year, monthIndex, 1));
  const monthEndExclusive = startOfDay(new Date(year, monthIndex + 1, 1));

  // Unique occupied dates (checkout-exclusive, clipped to month) — aligns with Analytics; never exceeds days in month
  const occupiedDates = new Set();
  const ownerStayDates = new Set();
  let ownerPayoutProrated = 0;
  let touristTaxesDue = 0;

  const list = Array.isArray(reservations) ? reservations : [];

  for (const r of list) {
    const checkIn = parseLocalDate(r.check_in_date ?? r.checkin_date);
    const checkOut = parseLocalDate(r.check_out_date ?? r.checkout_date);
    if (!checkIn || !checkOut || checkOut <= checkIn) continue;

    const stayStart = startOfDay(checkIn);
    const stayEnd = startOfDay(checkOut);
    const canonical = r.canonical_financial_summary ?? null;

    // Tourist tax: owner obligations only. Same precedence as Owner Payout modal.
    const checkInInMonth = stayStart >= monthStart && stayStart < monthEndExclusive;
    const tt = canonical?.tourist_tax;
    let resolved = 0;
    if (tt && typeof tt === 'object') {
      const sub = tt.tourist_tax_to_subtract;
      const amount = Number(tt.amount || 0);
      const ownerRemits = tt.owner_remits === true;
      if (sub !== undefined && sub !== null) {
        resolved = Number(sub) || 0;
      } else {
        resolved = ownerRemits ? amount : 0;
      }
    } else {
      resolved = Number(r.tourist_tax_to_subtract ?? 0) || 0;
    }
    if (resolved > 0 && checkInInMonth) {
      touristTaxesDue += resolved;
    }

    const nightsInMonth = occupiedNightsInMonth(stayStart, stayEnd, monthStart, monthEndExclusive);
    const totalNights = totalReservationNights(checkIn, checkOut);
    const isOwner = isOwnerStay(r);

    // Occupied nights / owner stay nights: only count reservations that have at least one night in month
    if (nightsInMonth > 0) {
      for (let t = Math.max(stayStart.getTime(), monthStart.getTime()); t < Math.min(stayEnd.getTime(), monthEndExclusive.getTime()); t += ONE_DAY_MS) {
        occupiedDates.add(t);
        if (isOwner) ownerStayDates.add(t);
      }
    }

    // Owner Payout: strict check-in attribution. Sum full owner_payout_total only for
    // reservations whose check-in date falls within the selected month (matches modal).
    if (!isOwner && checkInInMonth) {
      const payoutTotal = canonical?.owner_payout?.owner_payout_total;
      const payoutNum = typeof payoutTotal === 'number' && !Number.isNaN(payoutTotal)
        ? payoutTotal
        : parseFloat(payoutTotal);
      if (typeof payoutNum === 'number' && !Number.isNaN(payoutNum)) {
        ownerPayoutProrated += payoutNum;
      }
    }
  }

  const occupiedNights = occupiedDates.size;
  const ownerStayNights = ownerStayDates.size;

  const occupancyPct = totalDaysInMonth > 0
    ? (occupiedNights / totalDaysInMonth) * 100
    : 0;
  const ownerStayPct = occupiedNights > 0
    ? (ownerStayNights / occupiedNights) * 100
    : 0;

  return {
    totalDaysInMonth,
    occupiedNights,
    ownerStayNights,
    ownerStayPct,
    occupancyPct,
    ownerPayoutProrated,
    touristTaxesDue,
  };
}
